import { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api, apiHelpers, generatorHelpers, githubHelpers } from '@/lib/api';
import { qk } from '@/lib/queryKeys';
import { useAuthStore } from '@/stores/auth';
import { formatDate, getStatusColor, copyToClipboard, getProjectUrl } from '@/lib/utils';
import { can } from '@/types';
import type { Capability } from '@/types';
import IntegrationGuide from '@/components/projects/IntegrationGuide';
import { DEFAULT_TEMPLATES, resolveTemplate } from '@/lib/legal-templates';
import { RichTextEditor } from '@/components/ui/rich-text-editor';
import { PluginCard } from '@/components/plugins/PluginCard';
import DomainManager from '@/components/domains/DomainManager';
import { useConfirm, usePrompt } from '@/components/ui/confirm-provider';
import { apiErrorMessage } from '@/lib/conflict';
import { SuperAdminBillingPanel } from '@/components/billing/SuperAdminBillingPanel';
import {
  ArrowLeft,
  Settings,
  Mail,
  MessageSquare,
  Shield,
  BarChart3,
  Puzzle,
  Copy,
  Check,
  Key,
  ExternalLink,
  Loader2,
  Trash2,
  AlertTriangle,
  Code,
  Globe,
  Save,
  X,
  FileText,
  Sparkles,
  Upload,
  ShoppingCart,
  Package,
  GitBranch,
  RefreshCw,
  Unplug,
  History,
  Clock,
  CheckCircle2,
  AlertCircle,
  Plus,
  Pencil,
  Palette,
  Layers,
  HelpCircle,
  Shield as ShieldIcon,
  Star,
  Bug,
  RotateCcw,
  Search as SearchIcon,
  Bot,
  Ticket,
  Zap,
} from 'lucide-react';

// ── Query keys not yet in the shared qk factory ──
// Literal values match existing cross-file usages ('storefront-spec' is also
// read by CommerceSettings) so invalidations stay in sync.
// Promoted to the central factory — kept as aliases to minimize churn.
const PROJECT_USAGE_KEY = qk.projectUsage;
const STOREFRONT_SPEC_KEY = qk.storefrontSpec();
const GENERATION_JOBS_KEY = qk.generationJobs;

// Small stat tile for the "billable usage this month" card on the overview tab
function UsageStat({
  label,
  value,
  icon,
  muted = false,
}: {
  label: string;
  value: number;
  icon: React.ReactNode;
  muted?: boolean;
}) {
  return (
    <div className={`rounded-lg border p-3 ${muted ? 'bg-muted/30' : 'bg-card'}`}>
      <div className="text-muted-foreground flex items-center gap-1.5 text-xs">
        {icon}
        {label}
      </div>
      <div
        className={`mt-1 text-2xl font-bold tabular-nums ${muted && value === 0 ? 'text-muted-foreground' : ''}`}
      >
        {value.toLocaleString()}
      </div>
    </div>
  );
}

export default function ProjectDetail() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const promptDialog = usePrompt();
  const [copiedKey, setCopiedKey] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingLegalPage, setEditingLegalPage] = useState<{
    key: string;
    label: string;
    content: string;
    title: string;
  } | null>(null);

  // Sync active tab with URL hash (e.g. #settings, #ecommerce)
  const getTabFromHash = () => {
    const hash = window.location.hash.replace('#', '');
    return hash || new URLSearchParams(window.location.search).get('tab') || 'overview';
  };
  const [activeTab, setActiveTabState] = useState(getTabFromHash);

  const setActiveTab = useCallback((tab: string) => {
    setActiveTabState(tab);
    window.history.replaceState(null, '', `#${tab}`);
  }, []);

  // Handle browser back/forward
  useEffect(() => {
    const onHashChange = () => setActiveTabState(getTabFromHash());
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);
  const [localDevUrl, setLocalDevUrl] = useState('');
  const [industry, setIndustry] = useState('');
  const [localDevUrlInitialized, setLocalDevUrlInitialized] = useState(false);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [generatePrompt, setGeneratePrompt] = useState('');
  const [generateLoading, setGenerateLoading] = useState(false);
  const [generateFiles, setGenerateFiles] = useState<File[]>([]);
  /** When the backend reports an existing site, we surface this confirmation
   *  prompt instead of silently piling new files on top of the legacy code.
   *  Submitting the modal re-runs the generation with `confirmArchive: true`. */
  const [archiveConfirmation, setArchiveConfirmation] = useState<{
    plannedArchiveBranch: string;
    existingSite: {
      repoFullName?: string;
      lastCommitSha?: string;
      siteUrl?: string;
      repoUrl?: string;
    };
    hint?: string;
  } | null>(null);
  const [referenceUrls, setReferenceUrls] = useState<Array<{ url: string; description: string }>>(
    []
  );
  const [newRefUrl, setNewRefUrl] = useState('');

  // Copy AI Prompt state
  const [copiedPrompt, setCopiedPrompt] = useState(false);
  const [showPromptPreview, setShowPromptPreview] = useState(false);

  // Import website state
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importSource, setImportSource] = useState<string>('lovable');
  const [importLabel, setImportLabel] = useState('');
  const [importLoading, setImportLoading] = useState(false);

  // GitHub connection state
  const [ghStep, setGhStep] = useState<'select' | 'repos' | 'connected'>('select');
  const [ghRepos, setGhRepos] = useState<any[]>([]);
  const [ghReposLoading, setGhReposLoading] = useState(false);
  const [ghSelectedRepo, setGhSelectedRepo] = useState<any>(null);
  const [ghBranch, setGhBranch] = useState('main');
  const [ghAutoDeploy, setGhAutoDeploy] = useState(true);
  const [ghNeedsBuild, setGhNeedsBuild] = useState(false);
  const [ghConnection, setGhConnection] = useState<any>(null);
  const [ghSetupLoading, setGhSetupLoading] = useState(false);
  const [ghSyncLoading, setGhSyncLoading] = useState(false);

  const { data, isPending, error } = useQuery({
    queryKey: qk.project(projectId),
    queryFn: () => apiHelpers.getProject(projectId!),
    enabled: Boolean(projectId),
  });

  const { data: pluginsData } = useQuery({
    queryKey: qk.projectPlugins(projectId),
    queryFn: () => apiHelpers.getProjectPlugins(projectId!),
    enabled: Boolean(projectId),
  });

  // Platform usage (metered billing counters — emails sent etc.)
  const { data: usageData } = useQuery({
    queryKey: PROJECT_USAGE_KEY(projectId),
    queryFn: () => apiHelpers.getProjectUsage(projectId!),
    enabled: Boolean(projectId),
    refetchInterval: 60_000, // refresh once a minute
  });

  const { data: storefrontSpec } = useQuery({
    queryKey: STOREFRONT_SPEC_KEY,
    queryFn: async () => {
      const response = await generatorHelpers.getStorefrontSpec('text');
      return typeof response.data === 'string' ? response.data : response.data?.data?.spec || '';
    },
    staleTime: 60 * 60 * 1000,
  });

  const regenerateKeyMutation = useMutation({
    mutationFn: () => apiHelpers.regenerateApiKey(projectId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.project(projectId) });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => apiHelpers.deleteProject(projectId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.projects() });
      navigate('/projects');
    },
  });

  // ── Suspension (SUPER_ADMIN) — idempotent toggles, no version guard ────
  const suspendMutation = useMutation({
    mutationFn: (reason?: string) => apiHelpers.suspendProject(projectId!, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.project(projectId) });
      queryClient.invalidateQueries({ queryKey: qk.projects() });
      toast.success('Project suspended — site and APIs are now offline');
    },
    onError: (err) => toast.error(apiErrorMessage(err, 'Failed to suspend project')),
  });

  const unsuspendMutation = useMutation({
    mutationFn: () => apiHelpers.unsuspendProject(projectId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.project(projectId) });
      queryClient.invalidateQueries({ queryKey: qk.projects() });
      toast.success('Project unsuspended — site is back online');
    },
    onError: (err) => toast.error(apiErrorMessage(err, 'Failed to unsuspend project')),
  });

  const handleSuspend = async () => {
    const reason = await promptDialog({
      title: 'Suspend this project?',
      message:
        'The website, public APIs, and all background jobs (SEO, auto-blog, marketing emails, renewals) go offline immediately. All data is kept and this is reversible. Optionally note a reason:',
      placeholder: 'e.g. customer discontinuing — grace period until …',
      confirmLabel: 'Suspend project',
    });
    if (reason === null) return; // cancelled
    suspendMutation.mutate(reason.trim() || undefined);
  };

  const handleUnsuspend = async () => {
    if (
      !(await confirm({
        title: 'Lift the suspension?',
        message: 'The website and public APIs come back online on the next request.',
        confirmLabel: 'Unsuspend',
      }))
    ) {
      return;
    }
    unsuspendMutation.mutate();
  };

  // ── Archival (SUPER_ADMIN) — tears down live infra, keeps all data ─────
  const archiveMutation = useMutation({
    mutationFn: (reason?: string) => apiHelpers.archiveProject(projectId!, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.project(projectId) });
      queryClient.invalidateQueries({ queryKey: qk.projects() });
      toast.success('Project archived — live infrastructure removed, data retained');
    },
    onError: (err) => toast.error(apiErrorMessage(err, 'Failed to archive project')),
  });

  const unarchiveMutation = useMutation({
    mutationFn: () => apiHelpers.unarchiveProject(projectId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.project(projectId) });
      queryClient.invalidateQueries({ queryKey: qk.projects() });
      toast.success('Unarchive started — the site returns once the rebuild completes');
    },
    onError: (err) => toast.error(apiErrorMessage(err, 'Failed to unarchive project')),
  });

  const handleArchive = async () => {
    const reason = await promptDialog({
      title: 'Archive this project?',
      message:
        'Archiving removes the live infrastructure: custom domains are detached from Cloudflare, the site Worker is deleted, and the GitHub repo is archived (read-only). All data (content, orders, CRM, media, repo code) is kept. The site serves 410 Gone, so search engines will deindex it. Unarchiving re-provisions everything but requires a rebuild (minutes) and SEO restarts from scratch. Optionally note a reason:',
      placeholder: 'e.g. customer churned — retain data until …',
      confirmLabel: 'Archive project',
    });
    if (reason === null) return;
    archiveMutation.mutate(reason.trim() || undefined);
  };

  const handleUnarchive = async () => {
    if (
      !(await confirm({
        title: 'Unarchive this project?',
        message:
          'Custom domains are re-attached, the GitHub repo is un-archived, and a rebuild is triggered. The site comes back once the build completes (typically a few minutes).',
        confirmLabel: 'Unarchive',
      }))
    ) {
      return;
    }
    unarchiveMutation.mutate();
  };

  const updateProjectMutation = useMutation({
    mutationFn: (data: Record<string, any>) => apiHelpers.updateProject(projectId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.project(projectId) });
      queryClient.invalidateQueries({ queryKey: qk.projects() });
      toast.success('Project settings saved');
    },
    onError: () => {
      toast.error('Failed to save settings');
    },
  });

  // ── GitHub connection hooks (must come before early returns) ──────────

  /** Load repos after OAuth is done */
  const handleGhLoadRepos = useCallback(async () => {
    if (!projectId) return;
    setGhReposLoading(true);
    try {
      const res = await githubHelpers.listRepos(projectId);
      setGhRepos(res.data?.data || []);
    } catch (err: any) {
      toast.error('Failed to load repositories');
    } finally {
      setGhReposLoading(false);
    }
  }, [projectId]);

  /** Load existing connection when switching to GitHub source */
  const loadGhConnection = useCallback(async () => {
    if (!projectId) return;
    try {
      const res = await githubHelpers.getConnection(projectId);
      const conn = res.data?.data;
      if (conn && conn.repoFullName) {
        setGhConnection(conn);
        setGhBranch(conn.branch || 'main');
        setGhAutoDeploy(conn.autoDeploy ?? true);
        setGhNeedsBuild(conn.needsBuild ?? false);
        setGhStep('connected');
      } else if (conn) {
        setGhConnection(conn);
        setGhStep('repos');
        handleGhLoadRepos();
      } else {
        setGhStep('select');
        setGhConnection(null);
      }
    } catch {
      setGhStep('select');
      setGhConnection(null);
    }
  }, [projectId, handleGhLoadRepos]);

  /** Listen for GitHub OAuth popup callback */
  useEffect(() => {
    const handler = (e: MessageEvent) => {
      if (e.origin !== window.location.origin) return;
      if (e.data?.type !== 'github-oauth-callback') return;
      if (e.data.connected) {
        toast.success('GitHub account connected!');
        setGhStep('repos');
        handleGhLoadRepos();
      } else if (e.data.error) {
        toast.error(`GitHub connection failed: ${e.data.error}`);
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [projectId, handleGhLoadRepos]);

  /** When user picks "GitHub" source, check for existing connection */
  useEffect(() => {
    if (importSource === 'github' && showImportModal) {
      loadGhConnection();
    }
  }, [importSource, showImportModal, loadGhConnection]);

  // ── Derived data & early returns ──────────────────────────────────────

  const project = data?.data?.data;
  const plugins = pluginsData?.data?.data || [];

  // Initialize localDevUrl and industry from project data
  if (project && !localDevUrlInitialized) {
    setLocalDevUrl(project.localDevUrl || '');
    setIndustry(project.industry || '');
    setLocalDevUrlInitialized(true);
  }

  const handleSaveLocalDevUrl = () => {
    updateProjectMutation.mutate({ localDevUrl: localDevUrl || '' });
  };

  const handleSaveIndustry = () => {
    updateProjectMutation.mutate({ industry: industry || '' });
  };

  const handleCopyApiKey = async () => {
    if (project?.apiKey) {
      await copyToClipboard(project.apiKey);
      setCopiedKey(true);
      setTimeout(() => setCopiedKey(false), 2000);
    }
  };

  if (isPending) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="text-primary h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="py-12 text-center">
        <p className="text-red-500">Project not found</p>
        <Link to="/projects" className="text-primary mt-2 inline-block hover:underline">
          Back to projects
        </Link>
      </div>
    );
  }

  const hasEcommerce = plugins.some((p: any) => p.pluginId === 'ecommerce');
  const hasChatbot = plugins.some((p: any) => p.pluginId === 'chatbot');

  const allTabs: {
    id: string;
    label: string;
    icon: any;
    requires?: Capability;
    visible?: boolean;
  }[] = [
    { id: 'overview', label: 'Overview', icon: Settings },
    { id: 'versions', label: 'Versions', icon: History },
    // E-Commerce tab only appears when the storefront plugin is installed
    { id: 'ecommerce', label: 'E-Commerce', icon: ShoppingCart, visible: hasEcommerce },
    { id: 'domains', label: 'Domains', icon: Globe, requires: 'edit_project' },
    { id: 'integration', label: 'Integration', icon: Code, requires: 'edit_project' },
    { id: 'plugins', label: 'Plugins', icon: Puzzle, requires: 'manage_plugins' },
    { id: 'settings', label: 'Settings', icon: Settings, requires: 'edit_project' },
  ];
  const tabs = allTabs.filter((t) => {
    if (t.visible === false) return false;
    if (t.requires && !can(user, t.requires)) return false;
    return true;
  });

  const isSuperAdmin = user?.role === 'SUPER_ADMIN';
  const quickLinks = [
    // The customer journey: brief → agent build → live site → AI edits.
    {
      label: 'Website Studio',
      href: `/projects/${projectId}/website`,
      icon: Sparkles,
      highlight: true,
    },
    // Legacy pipeline + repo/ZIP import expose GitHub mechanics — platform admins only.
    ...(isSuperAdmin
      ? [
          { label: 'Legacy generator', href: '#generate', icon: Sparkles, highlight: true },
          { label: 'Import Website', href: '#import', icon: Upload, highlight: true },
        ]
      : []),
    {
      label: 'Brand Kit',
      href: `/projects/${projectId}/brand-kit`,
      icon: Palette,
      highlight: true,
    },
    { label: 'Page Ownership', href: `/projects/${projectId}/page-ownership`, icon: Layers },
    { label: 'Blog', href: `/projects/${projectId}/blog`, icon: FileText },
    { label: 'Newsletter', href: `/projects/${projectId}/newsletter`, icon: Mail },
    { label: 'Contacts', href: `/projects/${projectId}/contacts`, icon: MessageSquare },
    { label: 'Consent', href: `/projects/${projectId}/consent`, icon: Shield },
    { label: 'Analytics', href: `/projects/${projectId}/analytics`, icon: BarChart3 },
    { label: 'Speed Booster', href: `/projects/${projectId}/speed-booster`, icon: Zap },
    { label: 'FAQ', href: `/projects/${projectId}/faq`, icon: HelpCircle },
    {
      label: 'Privacy Requests',
      href: `/projects/${projectId}/privacy-requests`,
      icon: ShieldIcon,
    },
    // Single SEO entry point: the SEO Co-Pilot plugin page (dashboard,
    // settings, plans as tabs). Search-engine verification lives inside the
    // dashboard's Search engines card; /projects/:id/seo redirects there.
    { label: 'SEO Co-Pilot', href: `/projects/${projectId}/plugins/seo-copilot`, icon: SearchIcon },
    // Plugin-gated quick links — only shown when the plugin is installed
    ...(hasChatbot
      ? [
          {
            label: 'AI Chatbot',
            href: `/projects/${projectId}/chatbot`,
            icon: Bot,
            highlight: true,
          },
        ]
      : []),
  ];

  const handleGenerate = async (opts: { confirmArchive?: boolean } = {}) => {
    if (!generatePrompt.trim()) return;
    setGenerateLoading(true);
    try {
      const hasFiles = generateFiles.length > 0;
      const hasRefs = referenceUrls.length > 0;
      const confirmArchive = opts.confirmArchive === true;

      let res: Response;
      if (hasFiles) {
        // Use FormData for file uploads
        const formData = new FormData();
        formData.append('prompt', generatePrompt);
        if (project?.industry) formData.append('industry', project.industry);
        if (hasRefs) formData.append('referenceUrls', JSON.stringify(referenceUrls));
        if (confirmArchive) formData.append('confirmArchive', 'true');
        generateFiles.forEach((file) => {
          const key = file.name.toLowerCase().includes('logo') ? 'logo' : 'file';
          formData.append(key, file);
        });
        res = await fetch(
          `${import.meta.env.VITE_API_URL || '/api/v1'}/generator/${projectId}/start`,
          {
            method: 'POST',
            headers: { Authorization: `Bearer ${useAuthStore.getState().accessToken}` },
            body: formData,
          }
        );
      } else {
        // JSON body (no files)
        res = await fetch(
          `${import.meta.env.VITE_API_URL || '/api/v1'}/generator/${projectId}/start`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${useAuthStore.getState().accessToken}`,
            },
            body: JSON.stringify({
              prompt: generatePrompt,
              industry: project?.industry,
              referenceUrls: hasRefs ? referenceUrls : undefined,
              confirmArchive: confirmArchive || undefined,
            }),
          }
        );
      }

      const data = await res.json();

      // Existing-site safety net — backend asks the user to confirm the
      // current website will be archived before being replaced.
      if (
        res.status === 409 &&
        data?.error?.code === 'REQUIRES_CONFIRMATION' &&
        data?.data?.requiresConfirmation
      ) {
        setArchiveConfirmation({
          plannedArchiveBranch: data.data.plannedArchiveBranch,
          existingSite: data.data.existingSite || {},
          hint: data.data.hint,
        });
        return;
      }

      if (data.success) {
        toast.success(
          confirmArchive
            ? 'Previous website archived. New generation started.'
            : 'Generation started!'
        );
        setShowGenerateModal(false);
        setArchiveConfirmation(null);
        setGeneratePrompt('');
        setGenerateFiles([]);
        setReferenceUrls([]);
        navigate(`/projects/${projectId}/editor/${data.data.id}`);
      } else {
        toast.error(data.error?.message || 'Failed to start generation');
      }
    } catch {
      toast.error('Failed to start generation');
    } finally {
      setGenerateLoading(false);
    }
  };

  const buildAiPrompt = () => {
    const apiUrl = `${import.meta.env.VITE_API_URL || `${window.location.origin}/api/v1`}`;
    const apiKey = project?.apiKey || '<YOUR_API_KEY>';
    const projectName = project?.name || 'My Store';

    let prompt = `=== STOREFRONT BUILD INSTRUCTIONS ===

Build a modern, responsive e-commerce website for "${projectName}".

IMPORTANT: This website connects to an existing backend API. Use the integration spec below to connect all e-commerce features (products, cart, checkout, categories).

Replace these values in the spec:
- API_BASE_URL: ${apiUrl}
- API_KEY: ${apiKey}

After building, export the website as a ZIP file so it can be imported into our platform.

`;
    if (storefrontSpec) {
      prompt += storefrontSpec;
    }
    return prompt;
  };

  const handleCopyPrompt = async () => {
    try {
      await navigator.clipboard.writeText(buildAiPrompt());
      setCopiedPrompt(true);
      toast.success('AI prompt copied! Paste it into Lovable, v0, or any AI builder.');
      setTimeout(() => setCopiedPrompt(false), 3000);
    } catch {
      toast.error('Failed to copy — try the preview button instead');
    }
  };

  const handleImport = async () => {
    if (!importFile) return;
    setImportLoading(true);
    try {
      const res = await generatorHelpers.importWebsite(
        projectId!,
        importFile,
        importSource,
        importLabel || undefined
      );
      if (res.data.success) {
        toast.success(`Imported ${res.data.data.fileCount} files!`);
        setShowImportModal(false);
        setImportFile(null);
        setImportLabel('');
        navigate(`/projects/${projectId}/editor/${res.data.data.id}`);
      } else {
        toast.error(res.data.error?.message || 'Import failed');
      }
    } catch (err: any) {
      toast.error(err.response?.data?.error?.message || 'Failed to import website');
    } finally {
      setImportLoading(false);
    }
  };

  // ── GitHub connection handlers ──────────────────────────────────────────

  /** Open GitHub OAuth popup */
  const handleGhConnect = () => {
    const apiUrl = import.meta.env.VITE_API_URL || '/api/v1';
    const token = useAuthStore.getState().accessToken;
    const url = `${apiUrl}/github/connect?projectId=${projectId}&token=${token}`;
    const w = 600,
      h = 700;
    const left = window.screenX + (window.innerWidth - w) / 2;
    const top = window.screenY + (window.innerHeight - h) / 2;
    window.open(url, 'github-oauth', `width=${w},height=${h},left=${left},top=${top}`);
  };

  /** Set up chosen repo + webhook */
  const handleGhSetup = async () => {
    if (!projectId || !ghSelectedRepo) return;
    setGhSetupLoading(true);
    try {
      const res = await githubHelpers.setup(projectId, {
        repoOwner: ghSelectedRepo.owner,
        repoName: ghSelectedRepo.name,
        branch: ghBranch || 'main',
        autoDeploy: ghAutoDeploy,
        needsBuild: ghNeedsBuild,
      });
      if (res.data?.success) {
        const d = res.data.data;
        setGhConnection({
          ...ghConnection,
          repoFullName: d.repoFullName,
          branch: d.branch,
          autoDeploy: d.autoDeploy,
          needsBuild: d.needsBuild,
          deployHookToken: d.deployHookToken,
          actionsYaml: d.actionsYaml,
        });
        setGhStep('connected');
        toast.success(`Connected to ${d.repoFullName}`);
      } else {
        toast.error(res.data?.error?.message || 'Setup failed');
      }
    } catch (err: any) {
      toast.error(err.response?.data?.error?.message || 'Failed to set up repo');
    } finally {
      setGhSetupLoading(false);
    }
  };

  /** Manual sync */
  const handleGhSync = async () => {
    if (!projectId) return;
    setGhSyncLoading(true);
    try {
      const res = await githubHelpers.sync(projectId);
      if (res.data?.success) {
        toast.success('Sync started — new version importing');
        loadGhConnection();
      } else {
        toast.error(res.data?.error?.message || 'Sync failed');
      }
    } catch (err: any) {
      toast.error(err.response?.data?.error?.message || 'Failed to sync');
    } finally {
      setGhSyncLoading(false);
    }
  };

  /** Disconnect repo */
  const handleGhDisconnect = async () => {
    if (!projectId) return;
    try {
      await githubHelpers.disconnect(projectId);
      setGhConnection(null);
      setGhStep('select');
      setGhSelectedRepo(null);
      toast.success('GitHub repo disconnected');
    } catch {
      toast.error('Failed to disconnect');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link to="/projects" className="hover:bg-muted rounded-lg p-2 transition-colors">
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold">{project.name}</h1>
            <span className={`${getStatusColor(project.status)} rounded-full px-2 py-1 text-xs`}>
              {project.status}
            </span>
          </div>
          {(() => {
            const projectUrl = getProjectUrl(project);
            return projectUrl ? (
              <a
                href={projectUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:text-primary/80 flex items-center gap-1 text-sm font-medium"
              >
                <ExternalLink className="h-3 w-3" />
                {project.domain || 'Open Local Dev'}
              </a>
            ) : project.domain ? (
              <span className="text-muted-foreground flex items-center gap-1 text-sm">
                {project.domain}
              </span>
            ) : null;
          })()}
        </div>
      </div>

      {/* Quick Links */}
      {user?.role === 'SUPER_ADMIN' && (project as { teamId?: string })?.teamId && (
        <div className="mb-4">
          <SuperAdminBillingPanel teamId={(project as { teamId?: string }).teamId!} />
        </div>
      )}

      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
        {quickLinks.map((link) =>
          link.href === '#generate' ? (
            <button
              key={link.label}
              onClick={() => setShowGenerateModal(true)}
              className="flex items-center gap-3 rounded-lg border-2 border-blue-200 bg-blue-50 p-4 text-left transition-shadow hover:border-blue-300 hover:shadow-md"
            >
              <link.icon className="h-5 w-5 text-blue-600" />
              <span className="font-medium text-blue-700">{link.label}</span>
            </button>
          ) : link.href === '#import' ? (
            <button
              key={link.label}
              onClick={() => setShowImportModal(true)}
              className="flex items-center gap-3 rounded-lg border-2 border-teal-200 bg-teal-50 p-4 text-left transition-shadow hover:border-teal-300 hover:shadow-md"
            >
              <link.icon className="h-5 w-5 text-teal-600" />
              <span className="font-medium text-teal-700">{link.label}</span>
            </button>
          ) : (
            <Link
              key={link.label}
              to={link.href}
              className={
                (link as { highlight?: boolean }).highlight && link.href.endsWith('/website')
                  ? 'border-primary/40 bg-primary/5 hover:border-primary flex items-center gap-3 rounded-lg border-2 p-4 transition-shadow hover:shadow-md'
                  : 'bg-card flex items-center gap-3 rounded-lg border p-4 transition-shadow hover:shadow-md'
              }
            >
              <link.icon
                className={
                  (link as { highlight?: boolean }).highlight && link.href.endsWith('/website')
                    ? 'text-primary h-5 w-5'
                    : 'text-muted-foreground h-5 w-5'
                }
              />
              <span className="font-medium">{link.label}</span>
            </Link>
          )
        )}
      </div>

      {/* Generate Website Modal */}
      {showGenerateModal && (
        <>
          <div
            className="fixed inset-0 z-50 bg-black/50"
            onClick={() => setShowGenerateModal(false)}
          />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-card w-full max-w-lg rounded-xl border p-6 shadow-lg">
              <div className="mb-4 flex items-center justify-between">
                <h2 className="flex items-center gap-2 text-lg font-semibold">
                  <Sparkles className="h-5 w-5 text-blue-600" />
                  Generate Website
                </h2>
                <button
                  onClick={() => setShowGenerateModal(false)}
                  className="rounded p-1 hover:bg-gray-100"
                >
                  <X className="h-5 w-5 text-gray-400" />
                </button>
              </div>
              <p className="mb-4 text-sm text-gray-600">
                Describe the website you want to create. Our AI will research, design, code, and
                optimize it for you.
              </p>
              <textarea
                value={generatePrompt}
                onChange={(e) => setGeneratePrompt(e.target.value)}
                placeholder="e.g. A modern landing page for a premium coffee roastery with a dark elegant theme, showcasing single-origin beans and subscription plans..."
                rows={4}
                className="bg-background w-full resize-none rounded-lg border px-4 py-3 text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
              />

              {/* File Upload */}
              <div className="mt-3">
                <label className="mb-1 block text-xs font-medium text-gray-600">
                  Upload assets (optional) — logos, images, brand docs
                </label>
                <input
                  type="file"
                  multiple
                  accept="image/*,.pdf,.txt,.csv"
                  onChange={(e) => {
                    const files = Array.from(e.target.files || []);
                    setGenerateFiles((prev) => [...prev, ...files].slice(0, 10));
                  }}
                  className="w-full text-xs file:mr-3 file:rounded file:border-0 file:bg-blue-50 file:px-3 file:py-1.5 file:text-xs file:font-medium file:text-blue-700 hover:file:bg-blue-100"
                />
                {generateFiles.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {generateFiles.map((f, i) => (
                      <span
                        key={i}
                        className="inline-flex items-center gap-1 rounded bg-gray-100 px-2 py-0.5 text-xs"
                      >
                        {f.name.length > 20 ? `${f.name.slice(0, 20)}...` : f.name}
                        <button
                          onClick={() => setGenerateFiles((prev) => prev.filter((_, j) => j !== i))}
                          className="text-gray-400 hover:text-red-500"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Reference URLs */}
              <div className="mt-3">
                <label className="mb-1 block text-xs font-medium text-gray-600">
                  Reference websites (optional) — for design inspiration
                </label>
                <div className="flex gap-2">
                  <input
                    type="url"
                    value={newRefUrl}
                    onChange={(e) => setNewRefUrl(e.target.value)}
                    placeholder="https://example.com"
                    className="bg-background flex-1 rounded border px-3 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-blue-200"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && newRefUrl.trim()) {
                        e.preventDefault();
                        setReferenceUrls((prev) => [
                          ...prev,
                          { url: newRefUrl.trim(), description: '' },
                        ]);
                        setNewRefUrl('');
                      }
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      if (newRefUrl.trim()) {
                        setReferenceUrls((prev) => [
                          ...prev,
                          { url: newRefUrl.trim(), description: '' },
                        ]);
                        setNewRefUrl('');
                      }
                    }}
                    disabled={!newRefUrl.trim()}
                    className="rounded border px-3 py-1.5 text-xs font-medium hover:bg-gray-50 disabled:opacity-40"
                  >
                    Add
                  </button>
                </div>
                {referenceUrls.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {referenceUrls.map((ref, i) => (
                      <div
                        key={i}
                        className="flex items-center gap-2 rounded bg-gray-50 px-2 py-1 text-xs"
                      >
                        <ExternalLink className="h-3 w-3 text-gray-400" />
                        <span className="flex-1 truncate">{ref.url}</span>
                        <button
                          onClick={() => setReferenceUrls((prev) => prev.filter((_, j) => j !== i))}
                          className="text-gray-400 hover:text-red-500"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="mt-4 flex justify-end gap-3">
                <button
                  onClick={() => setShowGenerateModal(false)}
                  className="rounded-lg border px-4 py-2 text-sm font-medium hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleGenerate()}
                  disabled={generatePrompt.trim().length < 10 || generateLoading}
                  className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-40"
                >
                  {generateLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" /> Generating...
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-4 w-4" /> Generate
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Import Website Modal */}
      {showImportModal && (
        <>
          <div
            className="fixed inset-0 z-50 bg-black/50"
            onClick={() => setShowImportModal(false)}
          />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-card max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-xl border p-6 shadow-lg">
              <div className="mb-4 flex items-center justify-between">
                <h2 className="flex items-center gap-2 text-lg font-semibold">
                  <Upload className="h-5 w-5 text-teal-600" />
                  Import Website
                </h2>
                <button
                  onClick={() => setShowImportModal(false)}
                  className="rounded p-1 hover:bg-gray-100"
                >
                  <X className="h-5 w-5 text-gray-400" />
                </button>
              </div>
              <p className="mb-4 text-sm text-gray-600">
                Upload a ZIP or connect a GitHub repo. Every push to your branch will auto-import.
              </p>
              {/* Source selector */}
              <div className="mb-3">
                <label className="mb-1.5 block text-xs font-medium text-gray-600">Source</label>
                <div className="flex flex-wrap gap-2">
                  {[
                    { value: 'lovable', label: 'Lovable' },
                    { value: 'v0', label: 'v0' },
                    { value: 'bolt', label: 'Bolt' },
                    { value: 'nextjs', label: 'Next.js' },
                    ...(isSuperAdmin ? [{ value: 'github', label: 'GitHub Repo' }] : []),
                    { value: 'manual', label: 'Other / Manual' },
                  ].map((s) => (
                    <button
                      key={s.value}
                      onClick={() => setImportSource(s.value)}
                      className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${importSource === s.value ? 'border-teal-500 bg-teal-50 text-teal-700' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}
                    >
                      {s.value === 'github' && <GitBranch className="mr-1 inline h-3 w-3" />}
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* ── GitHub connection flow ─────────────────────────────── */}
              {importSource === 'github' ? (
                <div className="space-y-4">
                  {/* Step 1: Connect GitHub account */}
                  {ghStep === 'select' && (
                    <div className="flex flex-col items-center gap-3 rounded-lg border-2 border-dashed border-gray-300 p-6">
                      <GitBranch className="h-10 w-10 text-gray-400" />
                      <p className="text-center text-sm text-gray-600">
                        Connect your GitHub account to auto-import on every push.
                      </p>
                      <button
                        onClick={handleGhConnect}
                        className="flex items-center gap-2 rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800"
                      >
                        <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0 0 24 12c0-6.63-5.37-12-12-12Z" />
                        </svg>
                        Connect GitHub
                      </button>
                    </div>
                  )}

                  {/* Step 2: Pick a repository */}
                  {ghStep === 'repos' && (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <label className="text-xs font-medium text-gray-600">
                          Select Repository
                        </label>
                        <button
                          onClick={handleGhLoadRepos}
                          disabled={ghReposLoading}
                          className="text-xs text-teal-600 hover:underline"
                        >
                          {ghReposLoading ? 'Loading...' : 'Refresh'}
                        </button>
                      </div>
                      {ghReposLoading ? (
                        <div className="flex items-center justify-center py-8">
                          <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
                        </div>
                      ) : ghRepos.length === 0 ? (
                        <p className="py-4 text-center text-sm text-gray-500">
                          No repositories found.
                        </p>
                      ) : (
                        <div className="max-h-48 space-y-1 overflow-y-auto rounded-lg border p-1">
                          {ghRepos.map((repo: any) => (
                            <button
                              key={repo.fullName}
                              onClick={() => {
                                setGhSelectedRepo(repo);
                                setGhBranch(repo.defaultBranch || 'main');
                              }}
                              className={`flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm transition-colors ${ghSelectedRepo?.fullName === repo.fullName ? 'bg-teal-50 text-teal-700' : 'hover:bg-gray-50'}`}
                            >
                              <GitBranch className="h-3.5 w-3.5 flex-shrink-0 text-gray-400" />
                              <span className="truncate font-medium">{repo.fullName}</span>
                              {repo.private && (
                                <span className="ml-auto rounded bg-gray-100 px-1.5 py-0.5 text-[10px] text-gray-500">
                                  private
                                </span>
                              )}
                            </button>
                          ))}
                        </div>
                      )}

                      {ghSelectedRepo && (
                        <>
                          <div>
                            <label className="mb-1 block text-xs font-medium text-gray-600">
                              Branch
                            </label>
                            <input
                              type="text"
                              value={ghBranch}
                              onChange={(e) => setGhBranch(e.target.value)}
                              className="bg-background w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-200"
                            />
                          </div>
                          <div className="flex items-center gap-4">
                            <label className="flex items-center gap-2 text-sm">
                              <input
                                type="checkbox"
                                checked={ghAutoDeploy}
                                onChange={(e) => setGhAutoDeploy(e.target.checked)}
                                className="rounded border-gray-300"
                              />
                              Auto-deploy on push
                            </label>
                            <label className="flex items-center gap-2 text-sm">
                              <input
                                type="checkbox"
                                checked={ghNeedsBuild}
                                onChange={(e) => setGhNeedsBuild(e.target.checked)}
                                className="rounded border-gray-300"
                              />
                              Needs build step
                            </label>
                          </div>
                          {ghNeedsBuild && (
                            <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-700">
                              A GitHub Actions workflow will be generated to build your project and
                              deploy the output.
                            </p>
                          )}
                        </>
                      )}

                      <div className="flex justify-end gap-3 pt-1">
                        <button
                          onClick={() => {
                            setGhStep('select');
                            setGhSelectedRepo(null);
                          }}
                          className="rounded-lg border px-4 py-2 text-sm font-medium hover:bg-gray-50"
                        >
                          Back
                        </button>
                        <button
                          onClick={handleGhSetup}
                          disabled={!ghSelectedRepo || ghSetupLoading}
                          className="flex items-center gap-2 rounded-lg bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-700 disabled:opacity-40"
                        >
                          {ghSetupLoading ? (
                            <>
                              <Loader2 className="h-4 w-4 animate-spin" /> Setting up...
                            </>
                          ) : (
                            <>
                              <GitBranch className="h-4 w-4" /> Connect Repo
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Step 3: Connected — show status + actions */}
                  {ghStep === 'connected' && ghConnection && (
                    <div className="space-y-3">
                      <div className="rounded-lg border border-green-200 bg-green-50 p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-2">
                            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-green-100">
                              <GitBranch className="h-4 w-4 text-green-600" />
                            </div>
                            <div>
                              <p className="text-sm font-semibold text-green-800">
                                {ghConnection.repoFullName}
                              </p>
                              <p className="text-xs text-green-600">
                                branch: <span className="font-mono">{ghConnection.branch}</span>
                                {ghConnection.autoDeploy && ' · auto-deploy on'}
                              </p>
                            </div>
                          </div>
                          <span className="rounded-full bg-green-200 px-2 py-0.5 text-[10px] font-medium text-green-800">
                            Connected
                          </span>
                        </div>
                        {ghConnection.lastSyncAt && (
                          <p className="mt-2 text-xs text-green-600">
                            Last sync: {new Date(ghConnection.lastSyncAt).toLocaleString()}
                            {ghConnection.lastSyncCommit && (
                              <span className="ml-1 font-mono">
                                ({ghConnection.lastSyncCommit.slice(0, 7)})
                              </span>
                            )}
                          </p>
                        )}
                        {ghConnection.lastSyncError && (
                          <p className="mt-1 text-xs text-red-600">
                            Last error: {ghConnection.lastSyncError}
                          </p>
                        )}
                      </div>

                      {/* Actions YAML for needsBuild projects */}
                      {ghConnection.actionsYaml && (
                        <div className="space-y-1.5">
                          <label className="text-xs font-medium text-gray-600">
                            GitHub Actions Workflow
                          </label>
                          <p className="text-xs text-gray-500">
                            Copy this into{' '}
                            <span className="font-mono">.github/workflows/deploy.yml</span> in your
                            repo, then add <span className="font-mono">TYASHIN_DEPLOY_TOKEN</span>{' '}
                            as a repository secret.
                          </p>
                          <div className="relative">
                            <pre className="max-h-40 overflow-auto rounded-lg bg-gray-900 p-3 text-xs text-green-400">
                              {ghConnection.actionsYaml}
                            </pre>
                            <button
                              onClick={() => {
                                navigator.clipboard.writeText(ghConnection.actionsYaml);
                                toast.success('Workflow YAML copied');
                              }}
                              className="absolute right-2 top-2 rounded bg-gray-700 p-1 text-gray-300 hover:text-white"
                            >
                              <Copy className="h-3.5 w-3.5" />
                            </button>
                          </div>
                          {ghConnection.deployHookToken && (
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-gray-500">Deploy Token:</span>
                              <code className="rounded bg-gray-100 px-2 py-0.5 font-mono text-xs text-gray-700">
                                {ghConnection.deployHookToken.slice(0, 8)}...
                              </code>
                              <button
                                onClick={() => {
                                  navigator.clipboard.writeText(ghConnection.deployHookToken);
                                  toast.success('Deploy token copied');
                                }}
                                className="text-xs text-teal-600 hover:underline"
                              >
                                Copy full token
                              </button>
                            </div>
                          )}
                        </div>
                      )}

                      <div className="flex items-center gap-2 pt-1">
                        <button
                          onClick={handleGhSync}
                          disabled={ghSyncLoading}
                          className="flex items-center gap-2 rounded-lg border border-teal-200 bg-teal-50 px-3 py-1.5 text-xs font-medium text-teal-700 hover:bg-teal-100 disabled:opacity-50"
                        >
                          {ghSyncLoading ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <RefreshCw className="h-3.5 w-3.5" />
                          )}
                          Sync Now
                        </button>
                        <button
                          onClick={handleGhDisconnect}
                          className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-100"
                        >
                          <Unplug className="h-3.5 w-3.5" />
                          Disconnect
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                /* ── ZIP upload flow (original) ──────────────────────── */
                <>
                  <div className="mb-3">
                    <label className="mb-1.5 block text-xs font-medium text-gray-600">
                      Website ZIP file
                    </label>
                    <div
                      className={`flex min-h-[100px] cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed p-4 transition-colors ${importFile ? 'border-teal-300 bg-teal-50' : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'}`}
                      onClick={() => document.getElementById('import-file-input')?.click()}
                    >
                      <input
                        id="import-file-input"
                        type="file"
                        accept=".zip,application/zip,application/x-zip-compressed"
                        className="hidden"
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          if (f) setImportFile(f);
                        }}
                      />
                      {importFile ? (
                        <div className="flex items-center gap-2 text-center">
                          <Upload className="h-5 w-5 text-teal-600" />
                          <div>
                            <p className="text-sm font-medium text-teal-700">{importFile.name}</p>
                            <p className="text-xs text-teal-600">
                              {(importFile.size / (1024 * 1024)).toFixed(1)} MB
                            </p>
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setImportFile(null);
                            }}
                            className="ml-2 rounded p-0.5 text-gray-400 hover:text-red-500"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ) : (
                        <>
                          <Upload className="mb-2 h-8 w-8 text-gray-400" />
                          <p className="text-sm text-gray-500">
                            Drop a ZIP here or{' '}
                            <span className="text-teal-600 underline">browse</span>
                          </p>
                          <p className="mt-1 text-xs text-gray-400">
                            Must contain index.html. Max 50 MB.
                          </p>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="mb-4">
                    <label className="mb-1.5 block text-xs font-medium text-gray-600">
                      Label (optional)
                    </label>
                    <input
                      type="text"
                      value={importLabel}
                      onChange={(e) => setImportLabel(e.target.value)}
                      placeholder="e.g. Thridify e-commerce frontend"
                      className="bg-background w-full rounded-lg border px-3 py-2 text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-200"
                    />
                  </div>
                  <div className="flex justify-end gap-3">
                    <button
                      onClick={() => setShowImportModal(false)}
                      className="rounded-lg border px-4 py-2 text-sm font-medium hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleImport}
                      disabled={!importFile || importLoading}
                      className="flex items-center gap-2 rounded-lg bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-700 disabled:opacity-40"
                    >
                      {importLoading ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" /> Importing...
                        </>
                      ) : (
                        <>
                          <Upload className="h-4 w-4" /> Import
                        </>
                      )}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </>
      )}

      {/* Tabs */}
      <div className="border-b">
        <div className="flex gap-4">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`${activeTab === tab.id ? 'border-primary text-primary' : 'text-muted-foreground hover:text-foreground border-transparent'} flex items-center gap-2 border-b-2 px-4 py-3 font-medium transition-colors`}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="grid gap-6 lg:grid-cols-2">
          {/* Project Info */}
          <div className="bg-card rounded-xl border p-6">
            <h3 className="mb-4 font-semibold">Project Information</h3>
            <dl className="space-y-4">
              <div>
                <dt className="text-muted-foreground text-sm">Name</dt>
                <dd className="font-medium">{project.name}</dd>
              </div>
              {project.description && (
                <div>
                  <dt className="text-muted-foreground text-sm">Description</dt>
                  <dd>{project.description}</dd>
                </div>
              )}
              <div>
                <dt className="text-muted-foreground text-sm">Created</dt>
                <dd>{formatDate(project.createdAt)}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground text-sm">Last Updated</dt>
                <dd>{formatDate(project.updatedAt)}</dd>
              </div>
            </dl>
          </div>

          {/* API Key */}
          <div className="bg-card rounded-xl border p-6">
            <h3 className="mb-4 font-semibold">API Configuration</h3>
            <div className="space-y-4">
              <div>
                <label className="text-muted-foreground mb-2 block text-sm">API Key</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={project.apiKey}
                    readOnly
                    className="bg-muted flex-1 rounded-lg border px-3 py-2 font-mono text-sm"
                  />
                  <button
                    onClick={handleCopyApiKey}
                    className="hover:bg-muted rounded-lg border px-3 py-2 transition-colors"
                  >
                    {copiedKey ? (
                      <Check className="h-4 w-4 text-green-500" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>

              {can(user, 'edit_project') && (
                <button
                  onClick={() => regenerateKeyMutation.mutate()}
                  disabled={regenerateKeyMutation.isPending}
                  className="flex items-center gap-2 text-sm text-orange-600 hover:text-orange-700"
                >
                  <Key className="h-4 w-4" />
                  {regenerateKeyMutation.isPending ? 'Regenerating...' : 'Regenerate API Key'}
                </button>
              )}

              <div className="border-t pt-4">
                <h4 className="mb-2 text-sm font-medium">Allowed Domains</h4>
                <div className="flex flex-wrap gap-2">
                  {project.allowedDomains?.length ? (
                    project.allowedDomains.map((domain: string) => (
                      <span key={domain} className="bg-muted rounded px-2 py-1 text-sm">
                        {domain}
                      </span>
                    ))
                  ) : (
                    <span className="text-muted-foreground text-sm">No domains configured</span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Billable usage this month */}
          <div className="bg-card rounded-xl border p-6 lg:col-span-2">
            <div className="mb-4 flex items-center justify-between">
              <div>
                <h3 className="flex items-center gap-2 font-semibold">
                  <BarChart3 className="h-5 w-5 text-teal-500" />
                  Billable usage ·{' '}
                  {usageData?.data?.data?.period || new Date().toISOString().slice(0, 7)}
                </h3>
                <p className="text-muted-foreground mt-1 text-sm">
                  Metered resources used by this project this month. Counts reset on the 1st of each
                  month.
                </p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
              <UsageStat
                label="Emails sent"
                icon={<Mail className="h-4 w-4" />}
                value={usageData?.data?.data?.totals?.email ?? 0}
              />
              <UsageStat
                label="AI tokens"
                icon={<Sparkles className="h-4 w-4" />}
                value={usageData?.data?.data?.totals?.ai_tokens ?? 0}
                muted
              />
              <UsageStat
                label="Storage (MB)"
                icon={<Package className="h-4 w-4" />}
                value={usageData?.data?.data?.totals?.storage_mb ?? 0}
                muted
              />
              <UsageStat
                label="Build minutes"
                icon={<Code className="h-4 w-4" />}
                value={usageData?.data?.data?.totals?.build_min ?? 0}
                muted
              />
            </div>
            {Boolean(usageData?.data?.data?.breakdown?.length) && (
              <details className="mt-4 text-sm">
                <summary className="text-muted-foreground hover:text-foreground cursor-pointer">
                  Breakdown by category
                </summary>
                <ul className="mt-2 space-y-1">
                  {usageData?.data?.data?.breakdown?.map((row, i) => (
                    <li
                      key={i}
                      className="text-muted-foreground flex items-center justify-between border-t py-1.5"
                    >
                      <span>
                        <span className="font-mono text-xs">{row.resource}</span>
                        {row.category && <span className="ml-2 text-xs">· {row.category}</span>}
                      </span>
                      <span className="text-foreground font-medium tabular-nums">
                        {row.total.toLocaleString()}
                      </span>
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </div>

          {/* Copy AI Prompt — the manual Lovable/v0 hand-off. Superadmin-only now
              that customers build through Website Studio; the ZIP import it
              points at is superadmin-only too. */}
          {isSuperAdmin && (
            <div className="bg-card rounded-xl border p-6 lg:col-span-2">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <h3 className="mb-1 flex items-center gap-2 font-semibold">
                    <Sparkles className="h-5 w-5 text-purple-500" />
                    Build with External AI
                  </h3>
                  <p className="text-muted-foreground mb-3 text-sm">
                    Copy a ready-to-paste prompt with your API credentials and storefront
                    integration spec. Paste it into <strong>Lovable</strong>, <strong>v0</strong>,{' '}
                    <strong>Bolt</strong>, <strong>ChatGPT</strong>, or any AI builder. Then import
                    the ZIP back here.
                  </p>
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      onClick={handleCopyPrompt}
                      className="flex items-center gap-2 rounded-lg bg-purple-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-purple-700"
                    >
                      {copiedPrompt ? (
                        <>
                          <Check className="h-4 w-4" /> Copied!
                        </>
                      ) : (
                        <>
                          <Copy className="h-4 w-4" /> Copy AI Prompt
                        </>
                      )}
                    </button>
                    <button
                      onClick={() => setShowPromptPreview(!showPromptPreview)}
                      className="flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition-colors hover:bg-gray-50"
                    >
                      <Code className="h-4 w-4" />
                      {showPromptPreview ? 'Hide Preview' : 'Preview Prompt'}
                    </button>
                    <button
                      onClick={() => setShowImportModal(true)}
                      className="flex items-center gap-2 rounded-lg border border-teal-300 bg-teal-50 px-3 py-2 text-sm font-medium text-teal-700 transition-colors hover:bg-teal-100"
                    >
                      <Upload className="h-4 w-4" /> Import ZIP Back
                    </button>
                  </div>
                  <div className="mt-2 flex items-center gap-4 text-xs text-gray-400">
                    <span>1. Copy prompt</span>
                    <span>&rarr;</span>
                    <span>2. Paste in Lovable/v0/ChatGPT</span>
                    <span>&rarr;</span>
                    <span>3. Export ZIP</span>
                    <span>&rarr;</span>
                    <span>4. Import back here</span>
                  </div>
                </div>
              </div>
              {showPromptPreview && (
                <div className="mt-4 border-t pt-4">
                  <pre className="max-h-[300px] overflow-auto whitespace-pre-wrap rounded-lg border bg-gray-50 p-4 font-mono text-xs text-gray-700">
                    {buildAiPrompt()}
                  </pre>
                </div>
              )}
            </div>
          )}

          {/* Installed Plugins */}
          <div className="bg-card rounded-xl border p-6 lg:col-span-2">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="font-semibold">Installed Plugins</h3>
              <Link
                to={`/projects/${projectId}/plugins`}
                className="text-primary text-sm hover:underline"
              >
                Browse plugins
              </Link>
            </div>
            {plugins.length === 0 ? (
              <p className="text-muted-foreground py-8 text-center">
                No plugins installed yet.{' '}
                <Link
                  to={`/projects/${projectId}/plugins`}
                  className="text-primary hover:underline"
                >
                  Install your first plugin
                </Link>
              </p>
            ) : (
              <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                {plugins.map((plugin: any) => (
                  <div
                    key={plugin.id}
                    className="bg-muted/50 flex items-center gap-3 rounded-lg p-3"
                  >
                    <Puzzle className="text-muted-foreground h-5 w-5" />
                    <div className="flex-1">
                      <p className="text-sm font-medium">{plugin.name}</p>
                      <p className="text-muted-foreground text-xs">{plugin.category}</p>
                    </div>
                    <span
                      className={`${plugin.isEnabled ? 'bg-green-500' : 'bg-gray-400'} h-2 w-2 rounded-full`}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'versions' && <VersionsTab projectId={projectId!} />}

      {activeTab === 'ecommerce' && (
        <div className="bg-card rounded-xl border p-6">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <h3 className="font-semibold">E-Commerce</h3>
              <p className="text-muted-foreground text-sm">
                Manage your products, orders, and store settings.
              </p>
            </div>
          </div>
          <div className="grid gap-3 sm:grid-cols-3">
            <Link
              to={`/projects/${projectId}/ecommerce`}
              className="hover:bg-accent flex items-center gap-3 rounded-lg border p-4 transition-colors"
            >
              <ShoppingCart className="text-primary h-5 w-5" />
              <div>
                <p className="font-medium">Products</p>
                <p className="text-muted-foreground text-xs">Create & manage catalog</p>
              </div>
            </Link>
            <Link
              to={`/projects/${projectId}/ecommerce/orders`}
              className="hover:bg-accent flex items-center gap-3 rounded-lg border p-4 transition-colors"
            >
              <Package className="text-primary h-5 w-5" />
              <div>
                <p className="font-medium">Orders</p>
                <p className="text-muted-foreground text-xs">View & fulfill orders</p>
              </div>
            </Link>
            <Link
              to={`/projects/${projectId}/ecommerce/coupons`}
              className="hover:bg-accent flex items-center gap-3 rounded-lg border p-4 transition-colors"
            >
              <Ticket className="text-primary h-5 w-5" />
              <div>
                <p className="font-medium">Coupons</p>
                <p className="text-muted-foreground text-xs">Discount codes & promos</p>
              </div>
            </Link>
            <Link
              to={`/projects/${projectId}/ecommerce/settings`}
              className="hover:bg-accent flex items-center gap-3 rounded-lg border p-4 transition-colors"
            >
              <Settings className="text-primary h-5 w-5" />
              <div>
                <p className="font-medium">Store Settings</p>
                <p className="text-muted-foreground text-xs">Currency, tax & payments</p>
              </div>
            </Link>
            <Link
              to={`/projects/${projectId}/reviews`}
              className="hover:bg-accent flex items-center gap-3 rounded-lg border p-4 transition-colors"
            >
              <Star className="text-primary h-5 w-5" />
              <div>
                <p className="font-medium">Reviews</p>
                <p className="text-muted-foreground text-xs">Moderate product reviews</p>
              </div>
            </Link>
            <Link
              to={`/projects/${projectId}/errors`}
              className="hover:bg-accent flex items-center gap-3 rounded-lg border p-4 transition-colors"
            >
              <Bug className="text-primary h-5 w-5" />
              <div>
                <p className="font-medium">Site Errors</p>
                <p className="text-muted-foreground text-xs">Client & server error reports</p>
              </div>
            </Link>
            <Link
              to={`/projects/${projectId}/returns`}
              className="hover:bg-accent flex items-center gap-3 rounded-lg border p-4 transition-colors"
            >
              <RotateCcw className="text-primary h-5 w-5" />
              <div>
                <p className="font-medium">Returns</p>
                <p className="text-muted-foreground text-xs">RMA & return requests</p>
              </div>
            </Link>
          </div>
        </div>
      )}

      {activeTab === 'domains' && <DomainManager projectId={projectId!} />}

      {activeTab === 'integration' && (
        <div className="bg-card rounded-xl border p-6">
          <IntegrationGuide project={project} />
        </div>
      )}

      {activeTab === 'plugins' && (
        <div className="bg-card rounded-xl border p-6">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="font-semibold">Installed Plugins</h3>
            <Link
              to={`/projects/${projectId}/plugins`}
              className="bg-primary text-primary-foreground hover:bg-primary/90 flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors"
            >
              <Puzzle className="h-4 w-4" />
              Browse & Install Plugins
            </Link>
          </div>
          {plugins.length === 0 ? (
            <div className="text-muted-foreground py-8 text-center">
              <Puzzle className="mx-auto mb-4 h-12 w-12 opacity-50" />
              <p className="mb-2">No plugins installed yet.</p>
              <Link to={`/projects/${projectId}/plugins`} className="text-primary hover:underline">
                Install your first plugin
              </Link>
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {plugins.map((plugin: any) => (
                <PluginCard
                  key={plugin.id || plugin.pluginId}
                  plugin={plugin}
                  projectId={projectId!}
                  isInstalled={true}
                  showInstallButton={false}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'settings' && (
        <div className="space-y-6">
          {/* Development Settings */}
          <div className="bg-card rounded-xl border p-6">
            <h3 className="mb-4 flex items-center gap-2 font-semibold">
              <Globe className="h-5 w-5" />
              Development Settings
            </h3>
            <div className="space-y-4">
              <div>
                <label className="text-muted-foreground mb-2 block text-sm">
                  Local Development URL
                </label>
                <p className="text-muted-foreground mb-2 text-xs">
                  The URL where this project runs locally (e.g., http://localhost:8080). Used for
                  the "Open" link in development mode.
                </p>
                <div className="flex gap-2">
                  <input
                    type="url"
                    value={localDevUrl}
                    onChange={(e) => setLocalDevUrl(e.target.value)}
                    placeholder="http://localhost:3000"
                    className="bg-background focus:ring-primary flex-1 rounded-lg border px-3 py-2 text-sm focus:border-transparent focus:outline-none focus:ring-2"
                  />
                  <button
                    onClick={handleSaveLocalDevUrl}
                    disabled={updateProjectMutation.isPending}
                    className="bg-primary text-primary-foreground hover:bg-primary/90 flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors disabled:opacity-50"
                  >
                    {updateProjectMutation.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Save className="h-4 w-4" />
                    )}
                    Save
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Industry/Niche Settings */}
          <div className="bg-card rounded-xl border p-6">
            <h3 className="mb-4 flex items-center gap-2 font-semibold">
              <Settings className="h-5 w-5" />
              AI & Content Settings
            </h3>
            <div className="space-y-4">
              <div>
                <label className="text-muted-foreground mb-2 block text-sm">Industry / Niche</label>
                <p className="text-muted-foreground mb-2 text-xs">
                  Your business industry or niche. Used by AI features (like blog generation) to
                  create relevant content.
                </p>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={industry}
                    onChange={(e) => setIndustry(e.target.value)}
                    placeholder="e.g., E-commerce, SaaS, Healthcare, Education"
                    className="bg-background focus:ring-primary flex-1 rounded-lg border px-3 py-2 text-sm focus:border-transparent focus:outline-none focus:ring-2"
                  />
                  <button
                    onClick={handleSaveIndustry}
                    disabled={updateProjectMutation.isPending}
                    className="bg-primary text-primary-foreground hover:bg-primary/90 flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors disabled:opacity-50"
                  >
                    {updateProjectMutation.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Save className="h-4 w-4" />
                    )}
                    Save
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Legal Pages */}
          <div className="bg-card rounded-xl border p-6">
            <h3 className="mb-2 flex items-center gap-2 font-semibold">
              <FileText className="h-5 w-5" />
              Legal Pages & Copyright
            </h3>
            <p className="text-muted-foreground mb-4 text-xs">
              Add legal pages to your website. Click the + button to create a page with a default
              template. Pages are served as HTML at your public API.
            </p>

            <div className="space-y-3">
              {(
                [
                  {
                    key: 'termsAndConditions',
                    label: 'Terms & Conditions',
                    slug: 'terms-and-conditions',
                  },
                  { key: 'privacyPolicy', label: 'Privacy Policy', slug: 'privacy-policy' },
                  { key: 'returnPolicy', label: 'Return Policy', slug: 'return-policy' },
                ] as const
              ).map(({ key, label, slug }) => {
                const page = (project as any).legalPages?.[key];
                const isEnabled = page?.enabled;
                return (
                  <div
                    key={key}
                    className="flex items-center justify-between rounded-lg border p-4"
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className={`h-2 w-2 rounded-full ${isEnabled ? 'bg-green-500' : 'bg-gray-300'}`}
                      />
                      <div>
                        <p className="text-sm font-medium">{label}</p>
                        {isEnabled && (
                          <p className="text-muted-foreground text-xs">/public/legal/{slug}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {isEnabled ? (
                        <button
                          onClick={() =>
                            setEditingLegalPage({
                              key,
                              label,
                              content: page.content || '',
                              title: page.title || label,
                            })
                          }
                          className="text-primary hover:bg-primary/10 flex items-center gap-1 rounded px-3 py-1.5 text-sm font-medium transition-colors"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                          Edit
                        </button>
                      ) : (
                        <button
                          onClick={() => {
                            const template = DEFAULT_TEMPLATES[key] || '';
                            const contactEmail = project.domain
                              ? `support@${project.domain}`
                              : undefined;
                            const resolved = resolveTemplate(template, project.name, contactEmail);
                            updateProjectMutation.mutate({
                              legalPages: {
                                [key]: { enabled: true, title: label, content: resolved },
                              },
                            });
                          }}
                          className="bg-primary text-primary-foreground hover:bg-primary/90 flex items-center gap-1 rounded px-3 py-1.5 text-sm font-medium transition-colors"
                        >
                          <Plus className="h-3.5 w-3.5" />
                          Add
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Copyright Text */}
            <div className="mt-4 border-t pt-4">
              <label className="text-muted-foreground mb-2 block text-sm">Copyright Text</label>
              <p className="text-muted-foreground mb-2 text-xs">
                Use <code className="bg-muted rounded px-1">{'{year}'}</code> for the current year.
                Shown in the storefront footer bar.
              </p>
              <div className="flex gap-2">
                <input
                  type="text"
                  defaultValue={(project as any).legalPages?.copyrightText || ''}
                  placeholder={`© {year} ${project.name}. All rights reserved.`}
                  onBlur={(e) => {
                    const val = e.target.value;
                    if (val !== ((project as any).legalPages?.copyrightText || '')) {
                      updateProjectMutation.mutate({ legalPages: { copyrightText: val } });
                    }
                  }}
                  className="bg-background focus:ring-primary flex-1 rounded-lg border px-3 py-2 text-sm focus:border-transparent focus:outline-none focus:ring-2"
                />
              </div>
            </div>

            {/* Footer Bar Toggle */}
            <div className="mt-4 flex items-center justify-between rounded-lg border p-4">
              <div>
                <p className="text-sm font-medium">Auto-inject footer bar</p>
                <p className="text-muted-foreground text-xs">
                  Show policy links and copyright in your storefront automatically
                </p>
              </div>
              <button
                onClick={() => {
                  const current = (project as any).legalPages?.showFooterBar ?? false;
                  updateProjectMutation.mutate({ legalPages: { showFooterBar: !current } });
                }}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  (project as any).legalPages?.showFooterBar
                    ? 'bg-primary'
                    : 'bg-gray-300 dark:bg-gray-600'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    (project as any).legalPages?.showFooterBar ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            {/* Footer Bar Theme + Embed snippet */}
            {(project as any).legalPages?.showFooterBar && (
              <>
                {/* Theme customisation */}
                <FooterBarThemeEditor
                  saved={(project as any).legalPages?.footerBarTheme}
                  copyrightPreview={
                    (project as any).legalPages?.copyrightText?.replace(
                      /\{year\}/g,
                      String(new Date().getFullYear())
                    ) || `\u00A9 ${new Date().getFullYear()} ${project.name}`
                  }
                  onSave={(theme) => {
                    updateProjectMutation.mutate({ legalPages: { footerBarTheme: theme } });
                  }}
                  isSaving={updateProjectMutation.isPending}
                />

                {/* Embed snippet */}
                <div className="mt-3 rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-800 dark:bg-blue-900/20">
                  <p className="mb-1 text-sm font-medium">Embed snippet (optional)</p>
                  <p className="text-muted-foreground mb-3 text-xs">
                    For non-generated sites only. Websites built with our AI generator include the
                    footer automatically. For external sites, paste this before{' '}
                    <code className="bg-muted rounded px-1">&lt;/body&gt;</code>.
                  </p>
                  <div className="relative">
                    <pre className="overflow-x-auto whitespace-pre-wrap break-all rounded-md bg-gray-900 p-3 text-xs text-gray-100">
                      {`<script src="${import.meta.env.VITE_API_URL?.replace('/api/v1', '') || 'https://website-api.tyashin.com'}/widget/legal-footer.js" data-api-key="${project.apiKey || 'YOUR_API_KEY'}"></script>`}
                    </pre>
                    <button
                      onClick={() => {
                        const snippet = `<script src="${import.meta.env.VITE_API_URL?.replace('/api/v1', '') || 'https://website-api.tyashin.com'}/widget/legal-footer.js" data-api-key="${project.apiKey || 'YOUR_API_KEY'}"></script>`;
                        navigator.clipboard.writeText(snippet);
                        toast.success('Snippet copied to clipboard');
                      }}
                      className="absolute right-2 top-2 rounded bg-gray-700 px-2 py-1 text-xs text-gray-200 transition-colors hover:bg-gray-600"
                    >
                      Copy
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Lifecycle: suspension + archival (SUPER_ADMIN) */}
          {user?.role === 'SUPER_ADMIN' && (
            <div
              className={
                project.status === 'SUSPENDED' || project.status === 'ARCHIVED'
                  ? 'rounded-xl border border-amber-300 bg-amber-50 p-6 dark:border-amber-700 dark:bg-amber-900/20'
                  : 'rounded-xl border border-amber-200 bg-white p-6 dark:border-amber-800 dark:bg-gray-800'
              }
            >
              <h3 className="mb-4 flex items-center gap-2 font-semibold text-amber-700 dark:text-amber-400">
                <AlertTriangle className="h-5 w-5" />
                {project.status === 'ARCHIVED'
                  ? 'Project Archived'
                  : project.status === 'SUSPENDED'
                    ? 'Project Suspended'
                    : 'Suspend or Archive Project'}
              </h3>

              {project.status === 'ARCHIVED' ? (
                <>
                  <div className="mb-4 space-y-1 text-sm text-amber-800 dark:text-amber-300">
                    <p>
                      Archived{' '}
                      {project.archival?.archivedAt
                        ? `on ${formatDate(project.archival.archivedAt)}`
                        : ''}
                      {project.archival?.archivedByEmail
                        ? ` by ${project.archival.archivedByEmail}`
                        : ''}
                      .
                    </p>
                    {project.archival?.reason && (
                      <p>
                        <span className="font-medium">Reason:</span> {project.archival.reason}
                      </p>
                    )}
                    <p>
                      Live infrastructure was removed (custom domains detached, site Worker deleted,
                      repo archived). All data is retained. The site serves 410 Gone.
                    </p>
                    {project.archival?.teardown?.errors &&
                      project.archival.teardown.errors.length > 0 && (
                        <p className="text-red-700 dark:text-red-400">
                          Teardown warnings: {project.archival.teardown.errors.join('; ')}
                        </p>
                      )}
                  </div>
                  <button
                    onClick={handleUnarchive}
                    disabled={unarchiveMutation.isPending}
                    className="flex items-center gap-2 rounded-lg bg-amber-600 px-4 py-2 text-white transition-colors hover:bg-amber-700 disabled:opacity-50"
                  >
                    {unarchiveMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                    Unarchive Project
                  </button>
                </>
              ) : project.status === 'SUSPENDED' ? (
                <>
                  <div className="mb-4 space-y-1 text-sm text-amber-800 dark:text-amber-300">
                    <p>
                      Suspended{' '}
                      {project.suspension?.suspendedAt
                        ? `on ${formatDate(project.suspension.suspendedAt)}`
                        : ''}
                      {project.suspension?.suspendedByEmail
                        ? ` by ${project.suspension.suspendedByEmail}`
                        : ''}
                      .
                    </p>
                    {project.suspension?.reason && (
                      <p>
                        <span className="font-medium">Reason:</span> {project.suspension.reason}
                      </p>
                    )}
                    <p>
                      The website, public APIs, and background jobs are offline. All data is
                      retained.
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-3">
                    <button
                      onClick={handleUnsuspend}
                      disabled={unsuspendMutation.isPending}
                      className="flex items-center gap-2 rounded-lg bg-amber-600 px-4 py-2 text-white transition-colors hover:bg-amber-700 disabled:opacity-50"
                    >
                      {unsuspendMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                      Unsuspend Project
                    </button>
                    <button
                      onClick={handleArchive}
                      disabled={archiveMutation.isPending}
                      className="flex items-center gap-2 rounded-lg border border-amber-600 px-4 py-2 text-amber-700 transition-colors hover:bg-amber-100 disabled:opacity-50 dark:text-amber-400 dark:hover:bg-amber-900/30"
                    >
                      {archiveMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                      Archive Project
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <p className="mb-4 text-sm text-gray-600 dark:text-gray-400">
                    <span className="font-medium">Suspend</span> takes the website, public APIs, and
                    background jobs offline immediately without touching anything — instantly
                    reversible (grace period). <span className="font-medium">Archive</span> goes
                    further: it removes the live infrastructure (domains, Worker, repo access) while
                    keeping all data — reversible, but the site needs a rebuild to return.
                  </p>
                  <div className="flex flex-wrap gap-3">
                    <button
                      onClick={handleSuspend}
                      disabled={suspendMutation.isPending}
                      className="flex items-center gap-2 rounded-lg border border-amber-600 px-4 py-2 text-amber-700 transition-colors hover:bg-amber-50 disabled:opacity-50 dark:text-amber-400 dark:hover:bg-amber-900/20"
                    >
                      {suspendMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                      Suspend Project
                    </button>
                    <button
                      onClick={handleArchive}
                      disabled={archiveMutation.isPending}
                      className="flex items-center gap-2 rounded-lg border border-gray-400 px-4 py-2 text-gray-600 transition-colors hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700/40"
                    >
                      {archiveMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                      Archive Project
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Danger Zone */}
          {can(user, 'delete_project') && (
            <div className="rounded-xl border border-red-200 bg-red-50 p-6 dark:border-red-800 dark:bg-red-900/20">
              <h3 className="mb-4 flex items-center gap-2 font-semibold text-red-600 dark:text-red-400">
                <AlertTriangle className="h-5 w-5" />
                Danger Zone
              </h3>
              <p className="mb-4 text-sm text-red-600/80 dark:text-red-400/80">
                Deleting a project will permanently remove all associated data including
                subscribers, contacts, and consent records. This action cannot be undone.
              </p>
              <button
                onClick={() => setShowDeleteModal(true)}
                className="flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-white transition-colors hover:bg-red-700"
              >
                <Trash2 className="h-4 w-4" />
                Delete Project
              </button>
            </div>
          )}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {/* Legal Page Editor Modal */}
      {editingLegalPage && (
        <LegalPageEditor
          title={editingLegalPage.title}
          content={editingLegalPage.content}
          pageKey={editingLegalPage.key}
          projectName={project.name}
          contactEmail={project.domain ? `support@${project.domain}` : undefined}
          onSave={(content) => {
            updateProjectMutation.mutate({
              legalPages: {
                [editingLegalPage.key]: {
                  enabled: true,
                  title: editingLegalPage.title,
                  content,
                },
              },
            });
            setEditingLegalPage(null);
          }}
          onDisable={() => {
            updateProjectMutation.mutate({
              legalPages: {
                [editingLegalPage.key]: {
                  enabled: false,
                  title: editingLegalPage.title,
                  content: editingLegalPage.content,
                },
              },
            });
            setEditingLegalPage(null);
          }}
          onClose={() => setEditingLegalPage(null)}
        />
      )}

      {showDeleteModal && (
        <>
          <div
            className="fixed inset-0 z-50 bg-black/50"
            onClick={() => setShowDeleteModal(false)}
          />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-card w-full max-w-md rounded-xl border p-6 shadow-lg">
              <div className="mb-4 flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30">
                  <AlertTriangle className="h-5 w-5 text-red-600" />
                </div>
                <div>
                  <h3 className="font-semibold">Delete Project</h3>
                  <p className="text-muted-foreground text-sm">This action cannot be undone</p>
                </div>
              </div>
              <p className="mb-6 text-sm">
                Are you sure you want to delete <strong>{project.name}</strong>? All data will be
                permanently removed.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="hover:bg-muted flex-1 rounded-lg border px-4 py-2 font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => deleteMutation.mutate()}
                  disabled={deleteMutation.isPending}
                  className="flex flex-1 items-center justify-center rounded-lg bg-red-600 px-4 py-2 font-medium text-white hover:bg-red-700 disabled:opacity-50"
                >
                  {deleteMutation.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    'Delete'
                  )}
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Archive Confirmation Modal — surfaced when /start returns 409
          REQUIRES_CONFIRMATION because the project already has a deployed
          website. Confirming archives main → archive-{timestamp} and lets
          the new generation replace main with a clean tree. */}
      {archiveConfirmation && (
        <>
          <div
            className="fixed inset-0 z-50 bg-black/60"
            onClick={() => !generateLoading && setArchiveConfirmation(null)}
          />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-card w-full max-w-lg rounded-xl border p-6 shadow-lg">
              <div className="mb-4 flex items-start gap-3">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-600">
                  <RotateCcw className="h-5 w-5" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold">Replace existing website?</h2>
                  <p className="mt-1 text-sm text-gray-600">
                    This project already has a deployed website. Generating a fresh one will:
                  </p>
                </div>
              </div>

              <ul className="mb-4 space-y-2 rounded-lg border bg-gray-50 p-4 text-sm text-gray-700">
                <li className="flex gap-2">
                  <span className="text-amber-600">1.</span>
                  <span>
                    Snapshot the current <code className="rounded bg-white px-1">main</code> branch
                    into{' '}
                    <code className="rounded bg-white px-1 font-mono text-xs">
                      {archiveConfirmation.plannedArchiveBranch}
                    </code>{' '}
                    (your old code stays recoverable there).
                  </span>
                </li>
                <li className="flex gap-2">
                  <span className="text-amber-600">2.</span>
                  <span>
                    Replace <code className="rounded bg-white px-1">main</code> entirely with the
                    new generation — every file from the previous site will be removed from{' '}
                    <code className="rounded bg-white px-1">main</code>.
                  </span>
                </li>
                <li className="flex gap-2">
                  <span className="text-amber-600">3.</span>
                  <span>Trigger a fresh build &amp; deploy of the new site.</span>
                </li>
              </ul>

              {(archiveConfirmation.existingSite.siteUrl ||
                archiveConfirmation.existingSite.repoUrl) && (
                <div className="mb-4 space-y-1 text-xs text-gray-500">
                  {archiveConfirmation.existingSite.siteUrl && (
                    <div>
                      Current site:{' '}
                      <a
                        href={archiveConfirmation.existingSite.siteUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="text-blue-600 hover:underline"
                      >
                        {archiveConfirmation.existingSite.siteUrl}
                      </a>
                    </div>
                  )}
                  {archiveConfirmation.existingSite.repoUrl && (
                    <div>
                      GitHub repo:{' '}
                      <a
                        href={archiveConfirmation.existingSite.repoUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="text-blue-600 hover:underline"
                      >
                        {archiveConfirmation.existingSite.repoFullName ||
                          archiveConfirmation.existingSite.repoUrl}
                      </a>
                    </div>
                  )}
                </div>
              )}

              {archiveConfirmation.hint && (
                <p className="mb-4 rounded border border-blue-100 bg-blue-50 p-3 text-xs text-blue-700">
                  💡 To tweak the existing site instead, close this dialog and open the website
                  editor — chat refinement and section edits keep your current code intact.
                </p>
              )}

              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setArchiveConfirmation(null)}
                  disabled={generateLoading}
                  className="rounded-lg border px-4 py-2 text-sm font-medium hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleGenerate({ confirmArchive: true })}
                  disabled={generateLoading}
                  className="flex items-center gap-2 rounded-lg bg-amber-600 px-4 py-2 text-sm font-medium text-white hover:bg-amber-700 disabled:opacity-50"
                >
                  {generateLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" /> Archiving &amp; generating…
                    </>
                  ) : (
                    <>
                      <RotateCcw className="h-4 w-4" /> Archive &amp; replace
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ── Versions / Generation History Tab ──

interface GenerationJobSummary {
  id: string;
  prompt: string;
  status: string;
  progress: number;
  currentStep?: string;
  error?: { message: string };
  result?: { r2Prefix: string; files: { path: string; size: number }[]; previewUrl?: string };
  createdBy: string;
  createdAt: string;
  completedAt?: string;
}

function VersionsTab({ projectId }: { projectId: string }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; label: string } | null>(null);
  const limit = 20;

  const { data, isPending } = useQuery({
    queryKey: [...GENERATION_JOBS_KEY(projectId), page],
    queryFn: async () => {
      const res = await api.get(`/generator/${projectId}/jobs`, { params: { page, limit } });
      return res.data;
    },
    enabled: Boolean(projectId),
    // Keep the current page visible while the next page loads.
    placeholderData: keepPreviousData,
  });

  const deleteMutation = useMutation({
    mutationFn: (jobId: string) => api.delete(`/generator/${projectId}/jobs/${jobId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: GENERATION_JOBS_KEY(projectId) });
      toast.success('Version deleted');
      setDeleteTarget(null);
    },
    onError: () => {
      toast.error('Failed to delete version');
    },
  });

  const jobs: GenerationJobSummary[] = data?.data || [];
  const meta = data?.meta || { page: 1, limit, total: 0, totalPages: 1 };

  function statusIcon(status: string) {
    switch (status) {
      case 'complete':
        return <CheckCircle2 className="h-4 w-4 text-green-500" />;
      case 'error':
        return <AlertCircle className="h-4 w-4 text-red-500" />;
      default:
        return <Loader2 className="h-4 w-4 animate-spin text-blue-500" />;
    }
  }

  function statusLabel(status: string) {
    switch (status) {
      case 'complete':
        return 'Complete';
      case 'error':
        return 'Failed';
      case 'pending':
        return 'Pending';
      default:
        return status.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
    }
  }

  function formatRelative(dateStr: string) {
    const d = new Date(dateStr);
    const now = new Date();
    const diff = now.getTime() - d.getTime();
    const mins = Math.floor(diff / 60_000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 30) return `${days}d ago`;
    return d.toLocaleDateString();
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Storefront Versions</h3>
          <p className="text-muted-foreground text-sm">
            History of all website generations, imports, and updates.
          </p>
        </div>
        <span className="text-muted-foreground text-sm">
          {meta.total} total version{meta.total !== 1 ? 's' : ''}
        </span>
      </div>

      {isPending ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="text-muted-foreground h-8 w-8 animate-spin" />
        </div>
      ) : jobs.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-xl border py-16 text-center">
          <History className="text-muted-foreground mb-3 h-10 w-10" />
          <h4 className="text-sm font-medium">No versions yet</h4>
          <p className="text-muted-foreground mt-1 text-sm">
            Generate or import a website to see versions here.
          </p>
        </div>
      ) : (
        <>
          <div className="space-y-3">
            {jobs.map((job) => (
              <div
                key={job.id}
                className="bg-card hover:border-primary/30 group cursor-pointer rounded-xl border p-4 transition-colors"
                onClick={() => navigate(`/projects/${projectId}/editor/${job.id}`)}
              >
                <div className="flex items-start gap-3">
                  <div className="mt-0.5">{statusIcon(job.status)}</div>
                  <div className="min-w-0 flex-1">
                    <div className="mb-1 flex items-center gap-2">
                      <p className="truncate text-sm font-medium">{job.prompt}</p>
                      <span
                        className={`rounded-full px-1.5 py-0.5 text-xs ${
                          job.status === 'complete'
                            ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                            : job.status === 'error'
                              ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                              : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                        }`}
                      >
                        {statusLabel(job.status)}
                      </span>
                    </div>
                    <div className="text-muted-foreground flex items-center gap-3 text-xs">
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {formatRelative(job.createdAt)}
                      </span>
                      {job.result?.files && <span>{job.result.files.length} files</span>}
                      {job.error && (
                        <span className="max-w-[200px] truncate text-red-500">
                          {job.error.message}
                        </span>
                      )}
                      {job.status !== 'complete' && job.status !== 'error' && job.currentStep && (
                        <span className="text-blue-500">{job.currentStep}</span>
                      )}
                    </div>
                    {/* Progress bar for in-progress jobs */}
                    {job.status !== 'complete' && job.status !== 'error' && job.progress > 0 && (
                      <div className="bg-muted mt-2 h-1.5 w-full overflow-hidden rounded-full">
                        <div
                          className="h-full rounded-full bg-blue-500 transition-all"
                          style={{ width: `${job.progress}%` }}
                        />
                      </div>
                    )}
                  </div>
                  <button
                    className="text-muted-foreground rounded-md p-1.5 opacity-0 transition-all hover:bg-red-50 hover:text-red-600 group-hover:opacity-100 dark:hover:bg-red-900/20"
                    title="Delete version"
                    onClick={(e) => {
                      e.stopPropagation();
                      setDeleteTarget({
                        id: job.id,
                        label: job.prompt?.slice(0, 60) || 'Generation',
                      });
                    }}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Delete confirmation dialog */}
          {deleteTarget && (
            <>
              <div
                className="fixed inset-0 z-50 bg-black/50"
                onClick={() => setDeleteTarget(null)}
              />
              <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                <div className="bg-card w-full max-w-sm rounded-xl border p-6 shadow-lg">
                  <div className="mb-4 flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30">
                      <Trash2 className="h-5 w-5 text-red-600 dark:text-red-400" />
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold">Delete Version</h3>
                      <p className="text-muted-foreground text-xs">This cannot be undone</p>
                    </div>
                  </div>
                  <p className="text-muted-foreground mb-4 truncate text-sm">
                    {deleteTarget.label}
                  </p>
                  <div className="flex justify-end gap-2">
                    <button
                      className="hover:bg-muted rounded-lg border px-3 py-1.5 text-sm font-medium"
                      onClick={() => setDeleteTarget(null)}
                    >
                      Cancel
                    </button>
                    <button
                      className="rounded-lg bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
                      disabled={deleteMutation.isPending}
                      onClick={() => deleteMutation.mutate(deleteTarget.id)}
                    >
                      {deleteMutation.isPending ? 'Deleting…' : 'Delete'}
                    </button>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Pagination */}
          {meta.totalPages > 1 && (
            <div className="flex items-center justify-between border-t pt-4">
              <p className="text-muted-foreground text-sm">
                Page {meta.page} of {meta.totalPages}
              </p>
              <div className="flex items-center gap-2">
                <button
                  className="hover:bg-muted rounded-lg border px-3 py-1.5 text-sm font-medium disabled:cursor-not-allowed disabled:opacity-50"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => p - 1)}
                >
                  Previous
                </button>
                <button
                  className="hover:bg-muted rounded-lg border px-3 py-1.5 text-sm font-medium disabled:cursor-not-allowed disabled:opacity-50"
                  disabled={page >= meta.totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── Legal Page Editor Modal ──

function LegalPageEditor({
  title,
  content,
  pageKey,
  projectName,
  contactEmail,
  onSave,
  onDisable,
  onClose,
}: {
  title: string;
  content: string;
  pageKey: string;
  projectName: string;
  contactEmail?: string;
  onSave: (content: string) => void;
  onDisable: () => void;
  onClose: () => void;
}) {
  const [editContent, setEditContent] = useState(content);
  const [editorKey, setEditorKey] = useState(0);
  const confirm = useConfirm();

  const handleResetToDefault = async () => {
    if (
      !(await confirm({
        title: 'Reset to template?',
        message: 'This will replace the current content with the standard template. Continue?',
        danger: true,
      }))
    ) {
      return;
    }
    const template = DEFAULT_TEMPLATES[pageKey];
    if (!template) return;
    const resolved = resolveTemplate(template, projectName, contactEmail);
    setEditContent(resolved);
    setEditorKey((k) => k + 1); // force RTE to re-mount with new content
  };

  return (
    <>
      <div className="fixed inset-0 z-50 bg-black/50" onClick={onClose} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div
          className="bg-card flex h-[90vh] w-full max-w-4xl flex-col rounded-xl border shadow-lg"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between border-b px-6 py-4">
            <h2 className="text-lg font-semibold">Edit: {title}</h2>
            <div className="flex items-center gap-2">
              <button
                onClick={handleResetToDefault}
                className="text-muted-foreground hover:text-foreground rounded border px-2 py-1 text-xs transition-colors"
              >
                Reset to default
              </button>
              <button onClick={onClose} className="hover:bg-muted rounded p-1">
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
          <div className="flex-1 overflow-auto p-6">
            <RichTextEditor key={editorKey} content={editContent} onChange={setEditContent} />
          </div>
          <div className="flex items-center justify-between border-t px-6 py-4">
            <button onClick={onDisable} className="text-sm text-red-600 hover:text-red-700">
              Disable Page
            </button>
            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="hover:bg-muted rounded-lg border px-4 py-2 text-sm font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => onSave(editContent)}
                className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-lg px-4 py-2 text-sm font-medium transition-colors"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

// ── Footer Bar Theme Editor (local state + explicit save) ──

interface FooterBarTheme {
  bgColor?: string;
  textColor?: string;
  linkColor?: string;
}

function FooterBarThemeEditor({
  saved,
  copyrightPreview,
  onSave,
  isSaving,
}: {
  saved?: FooterBarTheme;
  copyrightPreview: string;
  onSave: (theme: FooterBarTheme) => void;
  isSaving: boolean;
}) {
  const [draft, setDraft] = useState<FooterBarTheme>({
    bgColor: saved?.bgColor || '#1f2937',
    textColor: saved?.textColor || '#d1d5db',
    linkColor: saved?.linkColor || '#93c5fd',
  });

  // Track whether the user changed anything since last save
  const isDirty =
    draft.bgColor !== (saved?.bgColor || '#1f2937') ||
    draft.textColor !== (saved?.textColor || '#d1d5db') ||
    draft.linkColor !== (saved?.linkColor || '#93c5fd');

  const fields = [
    { field: 'bgColor' as const, label: 'Background' },
    { field: 'textColor' as const, label: 'Text' },
    { field: 'linkColor' as const, label: 'Links' },
  ];

  return (
    <div className="mt-3 space-y-3 rounded-lg border p-4">
      <p className="text-sm font-medium">Footer bar theme</p>
      <div className="grid grid-cols-3 gap-4">
        {fields.map(({ field, label }) => (
          <div key={field}>
            <label className="text-muted-foreground mb-1 block text-xs">{label}</label>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={draft[field] || '#000000'}
                onChange={(e) => setDraft((prev) => ({ ...prev, [field]: e.target.value }))}
                className="h-8 w-10 cursor-pointer rounded border-0 bg-transparent p-0"
              />
              <span className="text-muted-foreground font-mono text-xs">{draft[field]}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Live preview strip */}
      <div
        className="flex items-center justify-between rounded-lg px-4 py-2 text-xs"
        style={{ background: draft.bgColor, color: draft.textColor }}
      >
        <span>{copyrightPreview}</span>
        <div className="flex gap-3">
          <span style={{ color: draft.linkColor }}>Terms</span>
          <span style={{ color: draft.linkColor }}>Privacy</span>
        </div>
      </div>

      {/* Save button */}
      <div className="flex justify-end">
        <button
          onClick={() => onSave(draft)}
          disabled={!isDirty || isSaving}
          className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-lg px-4 py-2 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isSaving ? 'Saving...' : isDirty ? 'Save Theme' : 'Saved'}
        </button>
      </div>
    </div>
  );
}
